import { mapProgramSourceToken, type ProgramFamily } from '@statura/experience-contract';
import type { SupabaseClient } from '@supabase/supabase-js';

import {
  authorityRevisionForProgramRows,
  type CurrentProviderProgramEnrollment,
} from '@/lib/navigation/provider-type';
import type { ProgramFirewall } from './current-program-write-authority';

export type CurrentProgramClientMemberships = ReadonlyMap<string, ReadonlySet<ProgramFirewall>> & {
  bindingCounts?: CurrentProgramClientBindingCounts;
  familyMemberships?: CurrentProgramClientFamilyMemberships;
  candidates?: CurrentProgramClientCandidates;
  membershipStatuses?: Readonly<Record<string, readonly ('ACTIVE' | 'PLANNED' | 'ENDED')[]>>;
};
export type CurrentProgramClientBindingCounts = Readonly<
  Record<string, Partial<Record<ProgramFirewall, number>>>
>;
export type CurrentProgramClientFamilyMemberships = Readonly<
  Record<string, readonly ProgramFamily[]>
>;
export type CurrentProgramClientCandidates = Readonly<
  Record<
    string,
    readonly {
      programEnrollmentId: string;
      family: ProgramFamily;
      firewall: ProgramFirewall;
      status?: 'ACTIVE' | 'PLANNED' | 'ENDED';
    }[]
  >
>;

function isProgramFirewall(value: unknown): value is ProgramFirewall {
  return value === 'SAH' || value === 'RAC' || value === 'OTHER';
}

/**
 * Keep the exact binding cardinality alongside the flattened membership map.
 * `OTHER` is intentionally not sufficient on its own: an NDIS + private client
 * has two active OTHER bindings and cannot be represented by the controlled
 * write RPC until Phase 3 adds an exact-program selector.
 */
export async function loadCurrentProgramClientBindingCounts(
  supabase: SupabaseClient,
  input: {
    tenantId: string;
    admittedFirewalls: readonly ('SAH' | 'RAC' | 'OTHER')[];
    authorityRevision: string;
    asOfDate: string;
  },
): Promise<CurrentProgramClientBindingCounts> {
  const { data, error } = await supabase
    .from('program_enrollments')
    .select('id, client_id, program_code, start_date, end_date')
    .eq('tenant_id', input.tenantId)
    .eq('status', 'ACTIVE')
    .lte('start_date', input.asOfDate)
    .or(`end_date.is.null,end_date.gte.${input.asOfDate}`);
  if (error || !Array.isArray(data)) throw new Error('program_scope_unavailable');
  const rows = data as CurrentProviderProgramEnrollment[];
  if (authorityRevisionForProgramRows(rows) !== input.authorityRevision) {
    throw new Error('program_scope_changed');
  }
  const counts: Record<string, Partial<Record<ProgramFirewall, number>>> = {};
  for (const row of rows) {
    const mapped = mapProgramSourceToken(row.program_code);
    if (mapped.overlay || !mapped.family || mapped.firewallFamily === 'UNKNOWN') continue;
    if (
      !input.admittedFirewalls.includes(mapped.firewallFamily) ||
      typeof row.client_id !== 'string' ||
      row.client_id.length === 0 ||
      !isProgramFirewall(mapped.firewallFamily)
    ) {
      continue;
    }
    const clientCounts = (counts[row.client_id] ??= {});
    clientCounts[mapped.firewallFamily] = (clientCounts[mapped.firewallFamily] ?? 0) + 1;
  }
  return counts;
}

export async function loadCurrentProgramClientMemberships(
  supabase: SupabaseClient,
  input: {
    tenantId: string;
    activeProgramFamily?: ProgramFamily;
    admittedFirewalls?: readonly ('SAH' | 'RAC' | 'OTHER')[];
    authorityRevision: string;
    asOfDate: string;
    admission?: 'ACTIVE' | 'SETUP_CARE' | 'RECORD_READ';
    /** Optional cancellation for the bounded navigation hint only. */
    signal?: AbortSignal;
  },
): Promise<CurrentProgramClientMemberships> {
  if (input.signal?.aborted) throw new Error('program_scope_unavailable');
  let query = supabase
    .from('program_enrollments')
    .select('id, client_id, program_code, status, start_date, end_date')
    .eq('tenant_id', input.tenantId)
    .eq('status', 'ACTIVE')
    .lte('start_date', input.asOfDate)
    .or(`end_date.is.null,end_date.gte.${input.asOfDate}`);
  if (input.signal) query = query.abortSignal(input.signal);
  const { data, error } = await query;
  if (input.signal?.aborted || error || !Array.isArray(data))
    throw new Error('program_scope_unavailable');

  const rows = data as CurrentProviderProgramEnrollment[];
  if (authorityRevisionForProgramRows(rows) !== input.authorityRevision) {
    throw new Error('program_scope_changed');
  }

  // Keep the active revision used by controlled money/agency RPCs unchanged.
  // Planned rows grant workspace admission only, including future assignment dates.
  if (input.admission === 'SETUP_CARE' || input.admission === 'RECORD_READ') {
    const planned = await supabase
      .from('program_enrollments')
      .select('id, client_id, program_code, status, start_date, end_date')
      .eq('tenant_id', input.tenantId)
      .eq('status', 'PLANNED');
    if (planned.error || !Array.isArray(planned.data)) throw new Error('program_scope_unavailable');
    const plannedClientIds = new Set(planned.data.map((row) => row.client_id));
    rows.push(
      ...planned.data.filter(
        (row) =>
          row.status === 'PLANNED' && mapProgramSourceToken(row.program_code).family === 'SAH',
      ),
    );
    // A newly registered or waitlisted resident holds a current PLANNED RAC
    // registration enrolment. It admits setup and care reads only while the
    // tenant's residential programme registration is LIVE (8 Oct 2026 owner
    // decision), mirroring loadRacAdmissionSetupClientScope. The DB setup-write
    // check internal.rac_planned_client_setup_allowed additionally requires an
    // ACTIVE RAC family, no ACTIVE family, or rac_admissions live; this
    // read-only admission does not. A failed read fails closed.
    const residentialAdmitted = input.admittedFirewalls?.length
      ? input.admittedFirewalls.includes('RAC')
      : input.activeProgramFamily === 'RAC';
    const plannedResidents = planned.data.filter(
      (row) =>
        residentialAdmitted &&
        row.status === 'PLANNED' &&
        (row.program_code === 'RAC_PERMANENT' || row.program_code === 'RAC_RESPITE') &&
        typeof row.start_date === 'string' &&
        row.start_date <= input.asOfDate &&
        (row.end_date == null || row.end_date >= input.asOfDate),
    );
    if (plannedResidents.length > 0) {
      const registration = await supabase
        .from('tenant_program_enrollments')
        .select('status')
        .eq('tenant_id', input.tenantId)
        .eq('program_key', 'residential')
        .eq('status', 'live');
      if (registration.error || !Array.isArray(registration.data)) {
        throw new Error('program_scope_unavailable');
      }
      if (registration.data.length === 1) rows.push(...plannedResidents);
    }
    if (input.admission === 'RECORD_READ') {
      // An exited home-care client keeps their record: the follow-up work, final
      // statement and tasks all point at it. Only the record, register and task reads
      // ask for this; pickers and write authority stay on the narrower admissions. A
      // client with a current enrolment elsewhere stays behind that lens.
      const ended = await supabase
        .from('program_enrollments')
        .select('id, client_id, program_code, status, start_date, end_date')
        .eq('tenant_id', input.tenantId)
        .eq('status', 'ENDED');
      if (ended.error || !Array.isArray(ended.data)) throw new Error('program_scope_unavailable');
      const admittedCurrentClientIds = new Set(rows.map((row) => row.client_id));
      rows.push(
        ...ended.data.filter(
          (row) =>
            !admittedCurrentClientIds.has(row.client_id) &&
            !plannedClientIds.has(row.client_id) &&
            mapProgramSourceToken(row.program_code).family !== 'RAC',
        ),
      );
    }
  }

  const membershipStatuses: Record<string, Set<'ACTIVE' | 'PLANNED' | 'ENDED'>> = {};
  const memberships = new Map<string, Set<ProgramFirewall>>();
  const bindingCounts: Record<string, Partial<Record<ProgramFirewall, number>>> = {};
  const familyMemberships: Record<string, Set<ProgramFamily>> = {};
  const candidates: Record<
    string,
    Array<{
      programEnrollmentId: string;
      family: ProgramFamily;
      firewall: ProgramFirewall;
      status?: 'ACTIVE' | 'PLANNED' | 'ENDED';
    }>
  > = {};
  for (const row of rows) {
    const mapped = mapProgramSourceToken(row.program_code);
    if (mapped.overlay) continue;
    if (!mapped.family || !isProgramFirewall(mapped.firewallFamily)) {
      throw new Error('program_scope_unmapped');
    }
    const admitted = input.admittedFirewalls;
    if (admitted?.length) {
      if (!admitted.includes(mapped.firewallFamily)) continue;
    } else if (mapped.family !== input.activeProgramFamily) continue;
    if (typeof row.client_id === 'string' && row.client_id.length > 0) {
      if (typeof row.id !== 'string' || row.id.length === 0) {
        throw new Error('program_scope_unavailable');
      }
      const current = memberships.get(row.client_id) ?? new Set<ProgramFirewall>();
      current.add(mapped.firewallFamily);
      memberships.set(row.client_id, current);
      const status =
        row.status === 'PLANNED' ? 'PLANNED' : row.status === 'ENDED' ? 'ENDED' : 'ACTIVE';
      (membershipStatuses[row.client_id] ??= new Set()).add(status);
      const counts = (bindingCounts[row.client_id] ??= {});
      if (status === 'ACTIVE')
        counts[mapped.firewallFamily] = (counts[mapped.firewallFamily] ?? 0) + 1;
      const families = (familyMemberships[row.client_id] ??= new Set<ProgramFamily>());
      families.add(mapped.family);
      const clientCandidates = (candidates[row.client_id] ??= []);
      clientCandidates.push({
        programEnrollmentId: row.id,
        family: mapped.family,
        firewall: mapped.firewallFamily,
        status,
      });
    }
  }

  const result = new Map(
    [...memberships.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([clientId, firewalls]) => [clientId, new Set([...firewalls].sort())]),
  ) as CurrentProgramClientMemberships;
  Object.defineProperty(result, 'membershipStatuses', {
    value: Object.fromEntries(
      Object.entries(membershipStatuses).map(([id, statuses]) => [id, [...statuses].sort()]),
    ),
  });
  Object.defineProperty(result, 'bindingCounts', { value: bindingCounts });
  Object.defineProperty(result, 'familyMemberships', {
    value: Object.fromEntries(
      Object.entries(familyMemberships).map(([clientId, families]) => [
        clientId,
        [...families].sort(),
      ]),
    ),
  });
  Object.defineProperty(result, 'candidates', {
    value: Object.fromEntries(
      Object.entries(candidates).map(([clientId, rows]) => [
        clientId,
        rows.sort((left, right) =>
          left.programEnrollmentId.localeCompare(right.programEnrollmentId),
        ),
      ]),
    ),
  });
  return result;
}

/** Compatibility read for exact-family consumers. New workspace code should use
 * memberships so a client with multiple current firewalls is never flattened. */
export async function loadCurrentProgramClientScope(
  supabase: SupabaseClient,
  input: {
    tenantId: string;
    activeProgramFamily?: ProgramFamily;
    admittedFirewalls?: readonly ProgramFirewall[];
    authorityRevision: string;
    asOfDate: string;
    admission?: 'ACTIVE' | 'SETUP_CARE' | 'RECORD_READ';
  },
): Promise<readonly string[]> {
  const memberships = await loadCurrentProgramClientMemberships(supabase, input);
  return [...memberships.keys()];
}
